/**
 * SearchDropdown Island — hono/jsx/dom
 *
 * Replaces public/search.js with a type-safe, declarative component.
 * Mounts onto the existing SSR-rendered #search-dropdown element.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'hono/jsx';
import { render } from 'hono/jsx/dom';
import { resizeProductImage } from '../../shared/image';
import type { SearchApiItem, SearchApiResponse } from '../../shared/search-api';

// ── Types ──────────────────────────────────────────
type DropdownState =
  | { kind: 'hidden' }
  | { kind: 'loading' }
  | { kind: 'results'; items: SearchApiItem[]; query: string };

// ── Constants ──────────────────────────────────────
const DEBOUNCE = 180;
const LOADING_DELAY = 120;
const MIN_CHARS = 2;
const LIMIT = 6;

// ── Helpers ────────────────────────────────────────
/** screen.md (tokens.ts) と同じブレークポイント */
const SCREEN_MD = '(max-width: 640px)';
const isMobile = () => window.matchMedia(SCREEN_MD).matches;

const formatPrice = (val: number | null): string => (val == null ? '— 円' : `実質 ${val.toLocaleString()} 円`);

// ── Sub-components ─────────────────────────────────
function SkeletonItems() {
  const shimmerStyle = {
    background: 'linear-gradient(90deg,var(--white4) 25%,var(--white12) 50%,var(--white4) 75%)',
    backgroundSize: '200% 100%',
    animation: 'sd-shimmer 1.4s ease-in-out infinite',
  };

  return (
    <>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '10px 14px',
            borderTop: i > 0 ? '1px solid var(--border)' : 'none',
          }}
        >
          <div
            style={{
              width: '56px',
              height: '75px',
              borderRadius: '6px',
              flexShrink: 0,
              ...shimmerStyle,
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                height: '14px',
                width: '70%',
                borderRadius: '4px',
                ...shimmerStyle,
              }}
            />
            <div
              style={{
                height: '12px',
                width: '40%',
                borderRadius: '4px',
                marginTop: '8px',
                ...shimmerStyle,
              }}
            />
          </div>
        </div>
      ))}
    </>
  );
}

function ResultItem(props: { item: SearchApiItem; idx: number; isActive: boolean; onHover: (idx: number) => void }) {
  const { item, idx, isActive, onHover } = props;
  const href = `/items/${encodeURIComponent(item.id)}`;
  const img = item.imageUrl ? resizeProductImage(item.imageUrl, 128) : null;

  return (
    <a
      href={href}
      data-href={href}
      class="sd-item"
      role="option"
      id={`sd-item-${idx}`}
      aria-selected={isActive}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 14px',
        borderTop: idx > 0 ? '1px solid var(--border)' : 'none',
        textDecoration: 'none',
        color: 'var(--text)',
        background: isActive ? 'var(--white8)' : 'none',
        transition: 'background 80ms ease',
      }}
      onMouseEnter={() => onHover(idx)}
      onMouseLeave={() => onHover(-1)}
    >
      {img ? (
        <img
          src={img}
          width={56}
          height={75}
          style={{
            borderRadius: '6px',
            objectFit: 'cover',
            flexShrink: 0,
            background: 'var(--card-gradient)',
          }}
          loading="lazy"
          alt=""
        />
      ) : (
        <div
          style={{
            width: '56px',
            height: '75px',
            borderRadius: '6px',
            flexShrink: 0,
            background: 'var(--card-gradient)',
          }}
        />
      )}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: 1.35,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.title}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '4px',
          }}
        >
          <span style={{ fontSize: '11px', color: 'var(--muted)' }}>{item.platform}</span>
          <span
            style={{
              fontSize: '12px',
              fontWeight: 700,
              fontFamily: 'var(--font-price)',
              color: 'var(--accent-light)',
            }}
          >
            {formatPrice(item.effectiveBest)}
          </span>
        </div>
      </div>
    </a>
  );
}

function EmptyResult() {
  return (
    <div
      style={{
        minHeight: '285px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        textAlign: 'center',
      }}
    >
      <div>
        <div style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: 1.5 }}>一致するタイトルが見つかりません</div>
        <div
          style={{
            fontSize: '12px',
            color: 'var(--muted)',
            opacity: 0.7,
            marginTop: '4px',
          }}
        >
          キーワードを変えて再検索してみてください
        </div>
      </div>
    </div>
  );
}

function SeeAllLink(props: { query: string; platform?: string }) {
  const params = new URLSearchParams({ q: props.query });
  if (props.platform) params.set('platform', props.platform);
  return (
    <a
      href={`/search?${params.toString()}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '12px 14px',
        fontSize: '13px',
        fontWeight: 600,
        color: 'var(--accent-light)',
        borderTop: '1px solid var(--border)',
        textDecoration: 'none',
        transition: 'background 80ms ease',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'var(--white8)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'none';
      }}
    >
      &ldquo;{props.query}&rdquo; の結果をすべて見る
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M5 12h14" />
        <path d="m12 5 7 7-7 7" />
      </svg>
    </a>
  );
}

// ── Main Island Component ──────────────────────────
function SearchDropdownIsland() {
  const [state, setState] = useState<DropdownState>({ kind: 'hidden' });
  const [activeIdx, setActiveIdx] = useState(-1);
  const seqRef = useRef(0) as { current: number };
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const stateKindRef = useRef(state.kind) as { current: DropdownState['kind'] };
  stateKindRef.current = state.kind;
  const activeIdxRef = useRef(activeIdx) as { current: number };
  activeIdxRef.current = activeIdx;

  const { inputEl, formEl, dropdownEl } = useMemo(
    () => ({
      inputEl: document.getElementById('search-input') as HTMLInputElement | null,
      formEl: document.getElementById('search-form') as HTMLFormElement | null,
      dropdownEl: document.getElementById('search-dropdown') as HTMLElement | null,
    }),
    [],
  );

  const hide = useCallback(() => {
    seqRef.current += 1;
    stateKindRef.current = 'hidden';
    activeIdxRef.current = -1;
    setState({ kind: 'hidden' });
    setActiveIdx(-1);
    if (inputEl) {
      inputEl.setAttribute('aria-expanded', 'false');
      inputEl.removeAttribute('aria-activedescendant');
    }
    document.body.classList.remove('search-open');
    document.body.style.overflow = '';
  }, [inputEl]);

  const doSearch = useCallback(
    (query: string) => {
      if (controllerRef.current) controllerRef.current.abort();
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
      controllerRef.current = new AbortController();
      const seq = ++seqRef.current;

      // Delayed skeleton
      loadingTimerRef.current = setTimeout(() => {
        if (seq === seqRef.current) {
          setState({ kind: 'loading' });
          if (inputEl) inputEl.setAttribute('aria-expanded', 'true');
        }
      }, LOADING_DELAY);

      const params = new URLSearchParams({ q: query, limit: String(LIMIT) });
      const platformInput = formEl?.querySelector<HTMLInputElement>('input[name="platform"]');
      if (platformInput?.value) params.set('platform', platformInput.value);

      fetch(`/api/search?${params.toString()}`, {
        signal: controllerRef.current.signal,
      })
        .then((r) => r.json())
        .then((data: SearchApiResponse | { error: string }) => {
          if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
          if (seq !== seqRef.current) return;
          setState({ kind: 'results', items: 'items' in data ? data.items : [], query });
          setActiveIdx(-1);
          if (inputEl) {
            inputEl.setAttribute('aria-expanded', 'true');
            inputEl.removeAttribute('aria-activedescendant');
          }
        })
        .catch((e: Error) => {
          if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
          if (e.name !== 'AbortError') hide();
        });
    },
    [inputEl, formEl, hide],
  );

  // ── Event listeners ────────────────────────────
  useEffect(() => {
    if (!inputEl || !formEl) return;

    const onInput = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const q = inputEl.value.trim();
      if (q.length < MIN_CHARS) {
        if (controllerRef.current) controllerRef.current.abort();
        hide();
        return;
      }
      timerRef.current = setTimeout(() => doSearch(q), DEBOUNCE);
    };

    const onFocus = () => {
      if (isMobile()) {
        document.body.classList.add('search-open');
        document.body.style.overflow = 'hidden';
      }
      const q = inputEl.value.trim();
      if (q.length >= MIN_CHARS && stateKindRef.current !== 'hidden') {
        if (inputEl) inputEl.setAttribute('aria-expanded', 'true');
      }
    };

    const onKeydown = (e: KeyboardEvent) => {
      if (stateKindRef.current === 'hidden') return;
      const items = dropdownEl?.querySelectorAll<HTMLAnchorElement>('.sd-item');
      const count = items?.length ?? 0;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (count === 0) return;
        setActiveIdx((prev) => {
          const next = (prev + 1) % count;
          activeIdxRef.current = next;
          inputEl?.setAttribute('aria-activedescendant', `sd-item-${next}`);
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (count === 0) return;
        setActiveIdx((prev) => {
          const next = prev <= 0 ? count - 1 : prev - 1;
          activeIdxRef.current = next;
          inputEl?.setAttribute('aria-activedescendant', `sd-item-${next}`);
          return next;
        });
      } else if (e.key === 'Enter' && activeIdxRef.current >= 0 && items?.[activeIdxRef.current]) {
        e.preventDefault();
        const href = items[activeIdxRef.current].dataset.href;
        if (href) location.href = href;
      } else if (e.key === 'Escape') {
        hide();
        inputEl.blur();
      }
    };

    const onDocClick = (e: MouseEvent) => {
      if (!formEl.contains(e.target as Node)) hide();
    };

    const onPageTransition = () => hide();
    const onSubmit = () => hide();

    inputEl.addEventListener('input', onInput);
    inputEl.addEventListener('focus', onFocus);
    inputEl.addEventListener('keydown', onKeydown);
    document.addEventListener('click', onDocClick);
    window.addEventListener('pageshow', onPageTransition);
    window.addEventListener('pagehide', onPageTransition);
    formEl.addEventListener('submit', onSubmit);

    return () => {
      inputEl.removeEventListener('input', onInput);
      inputEl.removeEventListener('focus', onFocus);
      inputEl.removeEventListener('keydown', onKeydown);
      document.removeEventListener('click', onDocClick);
      window.removeEventListener('pageshow', onPageTransition);
      window.removeEventListener('pagehide', onPageTransition);
      formEl.removeEventListener('submit', onSubmit);
      // Cleanup body state
      document.body.classList.remove('search-open');
      document.body.style.overflow = '';
      if (timerRef.current) clearTimeout(timerRef.current);
      if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
      if (controllerRef.current) controllerRef.current.abort();
    };
  }, [inputEl, formEl, dropdownEl, hide, doSearch]);

  // ── Sync hidden attribute with state ───────────
  useEffect(() => {
    if (!dropdownEl) return;
    dropdownEl.hidden = state.kind === 'hidden';
  }, [dropdownEl, state.kind]);

  // ── Render ─────────────────────────────────────
  if (state.kind === 'hidden') return null;

  if (state.kind === 'loading') {
    return <SkeletonItems />;
  }

  const { items, query } = state;
  if (items.length === 0) {
    return <EmptyResult />;
  }

  return (
    <>
      {items.map((item, idx) => (
        <ResultItem key={item.id} item={item} idx={idx} isActive={idx === activeIdx} onHover={setActiveIdx} />
      ))}
      <SeeAllLink query={query} platform={formEl?.querySelector<HTMLInputElement>('input[name="platform"]')?.value} />
    </>
  );
}

// ── Mount function ─────────────────────────────────
export function mountSearchDropdown(): void {
  const container = document.getElementById('search-dropdown');
  if (!container) return;
  render(<SearchDropdownIsland />, container);
}
